import React, { useState } from "react";
import {
  RiCheckboxFill,
  RiCheckboxBlankLine,
  RiArrowUpSLine,
  RiArrowDownSLine,
} from "react-icons/ri";
import { useTechFilter } from "../context/TechFilterContext";
import type { Division } from "../types";

type DivisionState = "none" | "partial" | "all";

export default function TechFilterSection({
  divisions,
}: {
  divisions: Division[];
}) {
  const { selectedEmployeeIds, toggleDivision, toggleEmployee, clearAll } =
    useTechFilter();
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  const toggleExpand = (id: number) =>
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const getDivisionState = (division: Division): DivisionState => {
    const empIds = division.employees.map((e) => e.id);
    const selected = empIds.filter((id) => selectedEmployeeIds.includes(id));
    if (selected.length === 0) return "none";
    if (selected.length === empIds.length) return "all";
    return "partial";
  };

  if (!divisions.length) return null;

  return (
    <div className="border-t border-gray-200">
      <div className="flex items-center justify-between border-b border-gray-200 px-2 py-2">
        <h4 className="m-0 text-sm text-gray-500">Division / Technician</h4>
        {selectedEmployeeIds.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="bg-transparent text-xs text-primary"
          >
            Clear Filter
          </button>
        )}
      </div>

      {divisions.map((division) => {
        const state = getDivisionState(division);
        const isExpanded = expandedIds.includes(division.id);
        const selectedCount = division.employees.filter((e) =>
          selectedEmployeeIds.includes(e.id)
        ).length;

        return (
          <div key={division.id}>
            <div className="flex w-full items-center gap-2 border-b border-gray-200 px-3 py-2">
              <input
                type="checkbox"
                className="cursor-pointer"
                checked={state === "all"}
                ref={(el) => {
                  if (el) el.indeterminate = state === "partial";
                }}
                onChange={() => toggleDivision(division)}
              />
              <button
                type="button"
                onClick={() => toggleExpand(division.id)}
                aria-expanded={isExpanded}
                className="flex flex-1 items-center gap-2 bg-transparent text-left"
              >
                <span className="flex-1 font-semibold">{division.label}</span>
                {selectedCount > 0 && (
                  <span className="text-sm font-semibold text-primary">
                    {selectedCount}
                  </span>
                )}
                {isExpanded ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
              </button>
            </div>

            {isExpanded &&
              division.employees.map((emp) => {
                const selected = selectedEmployeeIds.includes(emp.id);
                return (
                  <button
                    type="button"
                    key={emp.id}
                    onClick={() => toggleEmployee(emp.id)}
                    className={`flex w-full items-center gap-2 border-b border-gray-200 py-[7px] pl-7 pr-3 text-left ${
                      selected ? "text-primary" : "text-black"
                    }`}
                  >
                    {selected ? <RiCheckboxFill /> : <RiCheckboxBlankLine />}
                    {emp.label}
                  </button>
                );
              })}
          </div>
        );
      })}

      {selectedEmployeeIds.length > 0 && (
        <div className="px-3 py-1.5 text-xs text-primary">
          {selectedEmployeeIds.length} tech
          {selectedEmployeeIds.length > 1 ? "s" : ""} selected
        </div>
      )}
    </div>
  );
}
